import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Copy, Trash2 } from "lucide-react";
import { Toast } from "../../components/Toast";
import { convertCase, textCases, type TextCase } from "../../logic/textCase";

export function TextCaseConverter() {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const handleCopy = useCallback(
    async (value: string) => {
      if (!value) return;
      try {
        await navigator.clipboard.writeText(value);
        setToastMessage(t("textCaseConverter.copied"));
      } catch {
        setToastMessage(t("textCaseConverter.copyFailed"));
      }
    },
    [t],
  );

  return (
    <div className="text-case-converter">
      <div className="tcc-field">
        <label className="tcc-label" htmlFor="tcc-input">
          {t("textCaseConverter.inputLabel")}
        </label>
        <textarea
          id="tcc-input"
          className="tcc-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("textCaseConverter.inputPlaceholder")}
        />
        <div className="tcc-actions">
          <button
            type="button"
            className="tcc-action-btn"
            onClick={() => setInput("")}
            disabled={!input}
          >
            <Trash2 size={16} />
            {t("textCaseConverter.clear")}
          </button>
        </div>
      </div>

      <div className="tcc-results">
        {textCases.map((textCase: TextCase) => {
          const result = convertCase(input, textCase);
          return (
            <div className="tcc-result" key={textCase}>
              <div className="tcc-result-header">
                <span className="tcc-result-title">
                  {t(`textCaseConverter.cases.${textCase}`)}
                </span>
                <button
                  type="button"
                  className="tcc-action-btn"
                  onClick={() => handleCopy(result)}
                  disabled={!result}
                  aria-label={`${t("textCaseConverter.copy")} ${t(`textCaseConverter.cases.${textCase}`)}`}
                >
                  <Copy size={16} />
                  {t("textCaseConverter.copy")}
                </button>
              </div>
              <pre className="tcc-result-text">
                {result || t("textCaseConverter.outputPlaceholder")}
              </pre>
            </div>
          );
        })}
      </div>

      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage(null)} />
      )}
    </div>
  );
}
